import { Box, Skeleton } from "@mui/material";
import {
  MANUFACTURER_LOGO_SIZE_MAP,
  ManufacturerLogoSize,
} from "../utils/manufacturerLogoUtils";
import { useManufacturerLogo } from "../hooks/useManufacturerLogo";

export interface ManufacturerLogoProps {
  manufacturer: string | undefined | null;
  size: ManufacturerLogoSize;
  baseUrl: string;
  hidePlaceholder?: boolean;
}

/**
 * Displays a manufacturer logo with automatic theme support (light/dark mode).
 *
 * Fetches a `manifest.json` from the provided `baseUrl` to resolve logo filenames.
 * The manifest maps lowercased manufacturer names to light/dark image filenames.
 * Results are cached indefinitely via React Query, so the manifest is fetched once
 * per session regardless of how many instances are rendered.
 *
 * Renders a {@link Skeleton} while the manifest loads, a placeholder box when no
 * matching logo is found, and falls back to the placeholder on image load errors.
 *
 * @see {@link useManufacturerLogo} for the manifest lookup and theme resolution logic
 *
 * @example
 * // Basic usage in a grid
 * <ManufacturerLogo
 *   manufacturer="Dell"
 *   size="small"
 *   baseUrl={import.meta.env.LOGOS}
 * />
 *
 * @example
 * // In a drawer header
 * <ManufacturerLogo
 *   manufacturer={selectedEvent?.manufacturer}
 *   size="large"
 *   baseUrl={import.meta.env.LOGOS}
 * />
 */
const ManufacturerLogo = ({
  manufacturer,
  size,
  baseUrl,
  hidePlaceholder = false,
}: ManufacturerLogoProps) => {
  const dimension = MANUFACTURER_LOGO_SIZE_MAP[size];
  const placeholderBox = hidePlaceholder ? null : (
    <Box
      sx={{
        width: dimension,
        height: dimension,
        flexShrink: 0,
      }}
    />
  );

  if (!baseUrl) {
    console.error("Base URL for logos is not defined.");
    return placeholderBox;
  }

  const normalizedBaseUrl = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;

  const {
    isLoading,
    isError,
    imgError,
    src,
    alt,
    hasLogo,
    setImgError,
  } = useManufacturerLogo(manufacturer, normalizedBaseUrl);

  if (isLoading) {
    return <Skeleton variant="rounded" width={dimension} height={dimension} />;
  }

  if (isError || !hasLogo || imgError) {
    return placeholderBox;
  }

  return (
    <Box
      sx={{
        width: dimension,
        height: dimension,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={src!}
        alt={`${alt} logo`}
        onError={() => setImgError(true)}
        width={dimension}
        height={dimension}
        loading="lazy"
        style={{ objectFit: "contain" }}
      />
    </Box>
  );
};

export default ManufacturerLogo;
